import { defineStore } from 'pinia';
import { ChangeVehicleService as _changeVehicle } from '@/services/store/change-vehicle.service';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { ref } from 'vue';
import { IReviewSummary, ISitecoreReviewSummaryItem, OwnedAssistanceProduct } from '@/interfaces/entities';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';
import { useAccountStore } from '../account/account.state';
import { useFindCarStore } from '../find-car/find-car.state';
import { RoadsideVehicle } from '@/interfaces/entities/roadside-vehicle.interface';
import useVehicleEligibility from '@/composables/useVehicleEligibility';
import { getRoadsideProductNameFromProductCode } from '@/utils/roadside-helpers';
import { mapVehicle } from '@/utils/review-summary-mapping-util';
import { HOME_PAGE } from '@/constants/urls.const';
import { urlRedirect } from '@/utils/common-helper';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { FormEvents } from '@/enums/form-events.enum';

export const useChangeVehicleStore = defineStore('ChangeVehicle', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const accountStore = useAccountStore();
  const analyticsStore = useAnalyticsStore();
  const findCarStore = useFindCarStore();
  const currentPolicyId = ref<string>();
  const currentPolicyStatus = ref<string>();
  const roadsideVehicleRef = ref<any>();
  const rsaProductDetailsUrl = ref<string>();
  const currentPolicy = ref<OwnedAssistanceProduct>();
  /**
   * Get Policy
   */
  async function getCurrentPolicy(): Promise<void> {
    const isAuthenticated = await accountStore.isAuthenticated;
    if (!isAuthenticated) return;

    const identity = accountStore.identityDetails;
    if (!identity) return;

    const params = new URLSearchParams(location.search);
    currentPolicyId.value = params.get('policyId') ?? '';
    currentPolicyStatus.value = params.get('status') ?? '';

    if (!currentPolicyId.value) throw JP_GENERIC_CODE;

    const products = identity.portfolio?.assistanceProducts;
    if (!products) throw JP_GENERIC_CODE;

    const policy = products.find(
      (x) =>
        x.policyId === currentPolicyId.value &&
        (!currentPolicyStatus.value || x.status?.toLowerCase() === currentPolicyStatus.value.toLowerCase())
    );

    if (!policy) throw JP_GENERIC_CODE;

    currentPolicy.value = policy;
  }

  /**
   * Generate a review summary model for review and confirmation steps
   */
  async function getReviewSummaryModel(summaryFields: any): Promise<IReviewSummary[]> {
    const summary = {
      makeAndModel: `${currentPolicy.value?.vehicleDetail?.make.description} ${currentPolicy.value?.vehicleDetail?.model?.description} (${currentPolicy.value?.vehicleDetail?.registrationNumber})`,
      yearBody: `${currentPolicy.value?.vehicleDetail?.manufacturingYear} ${currentPolicy.value?.vehicleDetail?.variant?.description ?? ''} ${currentPolicy.value?.vehicleDetail?.option?.series ?? ''}`,
      registrationNumber: `${currentPolicy.value?.vehicleDetail?.registrationNumber}`,
    } as any;

    if (roadsideVehicleRef.value) {
      const vehicleDetails = mapVehicle(
        roadsideVehicleRef.value?.vehicleMake,
        roadsideVehicleRef.value?.vehicleModel,
        roadsideVehicleRef.value?.vehicleYear,
        roadsideVehicleRef.value?.vehicleColour,
        roadsideVehicleRef.value?.vehicleVariant
      );
      summary.makeModelYearColourRegistration = `${vehicleDetails}`;
      summary.registrationNumber = roadsideVehicleRef.value?.vehicleRegistration;
    }

    const summaryFieldsResult: IReviewSummary[] = summaryFields.map((field: any) => {
      const summaryItem = {
        formContentId: field.formContentId,
        summaryTitle: field.summaryTitle,
        icon: field.icon,
        items: field.items,
      };

      return replaceBookmarks(summaryItem, summary);
    });

    return summaryFieldsResult.map((field: any) => ({
      ...field,
      items: field.items.map((item: ISitecoreReviewSummaryItem) => ({
        ...item,
        ...item.fields,
        showEdit: false,
      })),
    }));
  }

  const { vehicleEligibility, setVehicleEligibility } = useVehicleEligibility();

  async function checkVehicleEligibility() {
    try {
      let isEligible = false;

      await setVehicleEligibility(
        roadsideVehicleRef?.value?.vehicleGlassesId ?? 0,
        getRoadsideProductNameFromProductCode(currentPolicy?.value?.productCode ?? '')
      );

      isEligible = vehicleEligibility?.value?.isEligible ?? false;
      return isEligible;
    } catch (error) {
      console.error('Error checking vehicle eligibility:', error);
      return false;
    }
  }

  function setVehicleDetails(roadsideVehicle: RoadsideVehicle) {
    roadsideVehicleRef.value = roadsideVehicle;
  }

  function clearVehicleDetails() {
    roadsideVehicleRef.value = undefined;
  }

  function setRsaProductDetailsUrl(url: string) {
    rsaProductDetailsUrl.value = url;
  }

  async function saveChangedVehicle() {
    try {
      const vehicle = findCarStore.selectedVehicle;
      const result = await _roadsideAssistanceService.upgradeRoadsideVehicle({
        vehicle: {
          id: currentPolicy?.value?.vehicleDetail?.id,
          nvic: vehicle?.nvic,
          registrationNumber:
            vehicle?.registrationNumber ||
            roadsideVehicleRef.value?.vehicleRegistration ||
            currentPolicy?.value?.vehicleDetail?.registrationNumber,
          stateCode: vehicle?.stateCode,
          make: { description: vehicle!.make.description, code: vehicle!.make.code },
          model: { description: vehicle!.model.description, code: vehicle!.model.code },
          body: {
            description: vehicle!.body.description,
            colour: findCarStore.vehicleColour || vehicle!.colour,
            code: vehicle!.body.code,
          },
          manufacturingYear: vehicle?.manufacturingYear,
          variant: { description: vehicle!.variant.description, code: vehicle!.variant.code },
          fuelType: { code: vehicle!.fuelType.code },
          kerbWeight: vehicle!.kerbWeight,
          kerbWeightUnit: vehicle!.kerbWeightUnit,
          length: vehicle!.length,
          lengthUnit: vehicle!.lengthUnit,
          loadCapacity: vehicle!.loadCapacity,
          loadCapacityUnit: vehicle!.loadCapacityUnit,
          option: { transmission: vehicle!.option.transmission, series: '', doors: '' },
        },
        apttusId: accountStore.identityDetails!.apttusId!,
        policyId: currentPolicyId.value!,
      });
      return result;
    } catch (error) {
      console.error('Failed to upgrade roadside for vehicle', error);
      return null;
    }
  }

  function redirectToSelfServiceRoadsideAssistanceProductDetail() {
    try {
      const queryParams: Record<string, string> = {
        policyId: currentPolicyId?.value ?? '',
      };

      const status = currentPolicyStatus?.value;
      if (status) {
        queryParams.status = status;
      }

      urlRedirect(rsaProductDetailsUrl?.value ?? '', queryParams);
    } catch (error) {
      console.warn('Unable to retrieve self service roadside assistance URL: ', error);
      // Session timeout
      urlRedirect(HOME_PAGE);
    }
  }

  const pushAnalytics = async (options: { event: FormEvents }) => {
    try {
      const firstVehicle = roadsideVehicleRef?.value;

      const analyticsModel: IFormAnalytics = {
        event: options.event,
        form: {
          productName: currentPolicy?.value?.tierDescription,
          termEndDate: new Date(currentPolicy?.value?.endDate ?? '')?.toDateString(),
          detail: {
            makeName: firstVehicle?.vehicleMake || currentPolicy.value?.vehicleDetail?.make?.description,
            modelName: firstVehicle?.vehicleModel || currentPolicy.value?.vehicleDetail?.model?.description,
            vehicleYear:
              firstVehicle?.vehicleYear?.toString() ||
              currentPolicy.value?.vehicleDetail?.manufacturingYear?.toString(),
          },
        },
      };
      analyticsStore.pushAnalytics(analyticsModel);
    } catch (e) {
      console.error('Error pushing to analytics', e);
    }
  };

  return {
    currentPolicy,
    roadsideVehicleRef,
    rsaProductDetailsUrl,
    getCurrentPolicy,
    getReviewSummaryModel,
    setVehicleDetails,
    clearVehicleDetails,
    checkVehicleEligibility,
    saveChangedVehicle,
    redirectToSelfServiceRoadsideAssistanceProductDetail,
    setRsaProductDetailsUrl,
    pushAnalytics,
  };
});
